import { useCalculatorStore, CalculationSequence, HistoryEntry } from '../store/calculatorStore';

export function SequencePanel() {
  const { sequences, history, runSequence, deleteSequence } = useCalculatorStore();

  return (
    <div className="sequence-panel">
      <div className="sequence-header">
        <h2>Calculation Sequences</h2>
        <p className="sequence-description">
          Sequences are ordered sets of calculations that can be re-run together.
          Select calculations from History to create a new sequence.
        </p>
      </div>

      <div className="sequence-list">
        {sequences.length === 0 ? (
          <div className="empty-state">
            <p>No sequences created yet</p>
            <p className="hint">
              Go to History, select calculations, and click "Create Sequence"
            </p>
          </div>
        ) : (
          sequences.map((sequence) => (
            <SequenceItem
              key={sequence.id}
              sequence={sequence}
              history={history}
              onRun={() => runSequence(sequence.id)}
              onDelete={() => deleteSequence(sequence.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface SequenceItemProps {
  sequence: CalculationSequence;
  history: HistoryEntry[];
  onRun: () => void;
  onDelete: () => void;
}

function SequenceItem({ sequence, history, onRun, onDelete }: SequenceItemProps) {
  const entries = sequence.entries
    .map((id) => history.find((h: HistoryEntry) => h.id === id))
    .filter((e): e is HistoryEntry => e !== undefined);

  return (
    <div className="sequence-item">
      <div className="sequence-item-header">
        <h3 className="sequence-name">{sequence.name}</h3>
        <div className="sequence-meta">
          <span>{entries.length} calculations</span>
          <span>Created {new Date(sequence.createdAt).toLocaleDateString()}</span>
        </div>
      </div>

      <div className="sequence-entries">
        {entries.slice(0, 5).map((entry, index) => (
          <div key={entry!.id} className="sequence-entry">
            <span className="entry-number">{index + 1}.</span>
            <code className="entry-input">{entry!.input}</code>
            <span className="entry-result">= {entry!.output}</span>
          </div>
        ))}
        {entries.length > 5 && (
          <div className="sequence-more">
            +{entries.length - 5} more calculations
          </div>
        )}
      </div>

      <div className="sequence-actions">
        <button className="btn-primary" onClick={onRun}>
          <PlayIcon /> Run Sequence
        </button>
        <button className="btn-danger" onClick={onDelete}>
          <DeleteIcon /> Delete
        </button>
      </div>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function DeleteIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    </svg>
  );
}
